import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiDownload, FiClock, FiFile, FiLink2, FiLoader, FiPackage } from "react-icons/fi";
import { transfersApi } from "../../services/transfersApi";
import { SHARE_URL } from "../../utils/appHost";
import { KIND_ICONS, formatBytes } from "../../components/share/fileKinds";
import "./share.css";

/** Branded header for public transfer pages: photographer logo (or Pic-Loud), name, subject. */
export function OwnerHeader({ owner, subject, children }) {
  return (
    <div className="shr-public-head">
      <img
        className="shr-owner-logo"
        src={owner.logo_url || "/logo_horizontal.png"}
        alt={owner.company_name || "Pic-Loud"}
      />
      {owner.company_name && <div className="shr-owner-name">{owner.company_name}</div>}
      <h1 className="shr-public-title">{subject}</h1>
      {children}
    </div>
  );
}

export function PublicFooter() {
  const { t } = useTranslation();
  return (
    <div className="shr-footer-cta">
      {t("share.public.cta_text")}
      <a href={`${SHARE_URL}/`}>{t("share.public.cta_btn")}</a>
    </div>
  );
}

export function PublicNotFound() {
  const { t } = useTranslation();
  return (
    <div className="shr-root">
      <main className="shr-main narrow">
        <div className="shr-center">
          <div className="shr-empty-icon"><FiLink2 size={22} /></div>
          <h1 className="shr-title">{t("share.public.not_found_title")}</h1>
          <p className="shr-muted">{t("share.public.not_found_desc")}</p>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

/** Download page for a "send" transfer (/t/:token). Files still uploading appear as they finish. */
export default function PublicTransferPage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | not_found

  useEffect(() => {
    let timer;
    const load = () =>
      transfersApi
        .getPublic(token)
        .then((res) => {
          if (res.data.kind !== "send") throw new Error("not a download link");
          setData(res.data);
          setStatus("ready");
          // Sender is still uploading: refresh until every file is there
          if (!res.data.is_complete) timer = setTimeout(load, 5000);
        })
        .catch(() => setStatus((s) => (s === "ready" ? s : "not_found")));
    load();
    return () => clearTimeout(timer);
  }, [token]);

  useEffect(() => {
    if (data?.subject) document.title = `${data.subject} · Pic-Loud Share`;
  }, [data]);

  if (status === "loading") {
    return <div className="shr-root"><div className="shr-center"><div className="shr-spinner" /></div></div>;
  }
  if (status === "not_found") return <PublicNotFound />;

  const doneFiles = data.files.filter((f) => f.status === "done");

  return (
    <div className="shr-root">
      <main className="shr-main narrow">
        <div className="shr-card">
          <OwnerHeader owner={data.owner} subject={data.subject}>
            <div className="shr-public-meta">
              <span><FiPackage /> {t("share.dashboard.files", { count: doneFiles.length })} · {formatBytes(data.total_bytes)}</span>
              <span>
                <FiClock />
                {data.expires_at
                  ? t("share.public.expires_on", { date: new Date(data.expires_at).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" }) })
                  : t("share.dashboard.never")}
              </span>
            </div>
          </OwnerHeader>

          {data.message && <p className="shr-public-message">{data.message}</p>}

          {!data.is_complete && (
            <div className="shr-callout info"><FiLoader size={18} /><span>{t("share.public.still_uploading")}</span></div>
          )}

          {doneFiles.length > 0 && (
            <a className="shr-btn primary block" href={transfersApi.publicZipUrl(token)}>
              <FiDownload /> {t("share.public.download_all")}
            </a>
          )}

          <ul className="shr-files">
            {data.files.map((f) => {
              const Icon = KIND_ICONS[f.category] || FiFile;
              const ready = f.status === "done";
              return (
                <li key={f.file_id} className="shr-file">
                  <span className={`shr-file-icon ${f.category}`}><Icon size={16} /></span>
                  <div className="shr-file-body">
                    <div className="shr-file-name" title={f.name}>{f.name}</div>
                    <div className="shr-file-meta">
                      {formatBytes(f.size)}
                      {!ready && ` · ${t("share.public.uploading")}`}
                    </div>
                  </div>
                  {ready && (
                    <a className="shr-icon-btn" href={transfersApi.publicFileUrl(token, f.file_id)} aria-label={t("share.public.download")} title={t("share.public.download")}>
                      <FiDownload size={15} />
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
